import { defineComponent as _defineComponent } from 'vue'
import { computed, onUnmounted, reactive, watch } from 'vue';
import ElementLibrarySelector from '@/components/DesignStudio/ElementLibrarySelector.vue';
import CInput from '@/components/shared/CInput.vue';
import CButton from '@/components/shared/CButton/CButton.vue';
import { LibraryTypeEnum } from '@/utils/enums/libraryType.enum';
import type { CanvasEx, PastedVector } from '@/typings/canvas';
import type { UserVector, VectorPlaceholderElement } from '@/typings/customilyElements/canvasElements';
import type { ElementMapOption } from './types';
import type { KeyValueDto } from '@/typings/keyValueDto';
import { getSvgStringFromPath, uploadVectorToS3 } from '@/utils/vectorUtils';
import { getExtension } from '@/utils/pathUtils';


export default /*#__PURE__*/_defineComponent({
  __name: 'DynamicVectorProperties',
  props: {
    canvas: null,
    vectorElement: null,
    editMode: { type: Boolean },
    previewMode: { type: Boolean }
  },
  emits: ["open-vector-library-selector", "trigger-loading", "display-sample-option", "image-replace"],
  setup(__props: any, { emit }: { emit: ({
    (e: 'open-vector-library-selector'): void;
    (e: 'trigger-loading', isLoading: boolean, msg?: string): void;
    (e: 'display-sample-option'): void;
    (e: 'image-replace'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
    canvas: CanvasEx;
    vectorElement: VectorPlaceholderElement;
    editMode?: boolean;
    previewMode?: boolean;
};

const VECTOR_TYPE = LibraryTypeEnum.Vector;





const model = reactive({
    vectors: [] as UserVector[],
    elementId: 1,
    vectorOption: 0,
    vectorMap: new Map<number, string>(),
    vectorLibraryId: undefined as number | undefined
});

const cachedSvg = new Map<string, string>();

const disabled = computed(() => !!props.vectorElement?.locked);

const onVectorPasted = ({ element, vectors }: PastedVector) => onVectorUpload(vectors, undefined, element);
watch(
    () => props.canvas,
    (canvas) => {
        if (!canvas) return;
        canvas.on('vectors:pasted', onVectorPasted);
    }
);

watch(
    () => props.vectorElement,
    (element) => {
        if (!element || element.starMapConfig || element.allowUserUpload) return;

        model.elementId = element.id;
        model.vectorOption = element.selectedPosition;
        model.vectorLibraryId = element.vectorLibraryId;
        initVectorMap(element.vectors);
    }
);

const initVectorMap = (vectors: UserVector[]) => {
    model.vectors = [...vectors];
    model.vectorMap = new Map(model.vectors.map((v) => [v.position + 1, v.svgPath]));
};

const displayLoader = (active = true, message?: string) => emit('trigger-loading', active, message);

const onVectorUpload = async (files: File[], position?: number, el?: VectorPlaceholderElement) => {
    const element = el || props.vectorElement;
    let imgPosition = position ? position - 1 : element.vectors.length;

    try {
        for (const file of files) {
            const selectedVector = position ? element.vectors.find((v) => v.position == imgPosition) : undefined;
            const extension = getExtension(file.name);
            let baseLoaderMsg = `Uploading ${extension.toUpperCase()} '${file.name}'`;
            displayLoader(true, baseLoaderMsg);
            const { SVG, EPS } = await uploadVectorToS3(file, false, false, undefined, (type, progress) => {
                if (type == 'upload') {
                    displayLoader(true, `${baseLoaderMsg} ${progress}%`);
                    return;
                }

                displayLoader(true, 'Processing');
            });

            if (selectedVector) {
                selectedVector.svgPath = SVG;
                selectedVector.epsPath = EPS;
                imgPosition++;
                continue;
            }

            element.vectors.push({
                svgPath: SVG,
                epsPath: EPS,
                position: imgPosition
            });
            imgPosition++;
        }

        initVectorMap(element.vectors);
        const lastVector = model.vectors.at(-1)!;
        await loadVectorIntoElement(lastVector.position + 1, lastVector.svgPath, element);

        if (props.editMode) {
            element.setEditedProp('vectors');
        }
    } catch (error) {
        console.error(error);
    } finally {
        displayLoader(false);
    }
};

const loadVectorIntoElement = async (option: number, value?: ElementMapOption, el?: VectorPlaceholderElement) => {
    const element = el || props.vectorElement;
    const vector = (value as string) || model.vectorMap.get(option);

    if (!vector?.length) {
        element.selectedPosition = model.vectorOption = 1;
        emit('display-sample-option');
        return;
    }

    element.selectedPosition = option;
    model.vectorOption = option;

    const svgString = cachedSvg.get(vector) || (await getSvgStringFromPath(vector));
    cachedSvg.set(vector, svgString);

    element.set('svgString', svgString);
    props.canvas.requestRenderAll();
};

const removeVectorOptionFromMap = (position: number) => {
    const element = props.vectorElement;
    if (!element) return;

    element.vectors = element.vectors.filter((v) => v.position != position - 1);
    initVectorMap(element.vectors);

    if (props.editMode) {
        element.setEditedProp('vectors');
    }

    loadVectorIntoElement(1);
};

const onVectorOptionsReordered = (options: KeyValueDto<number, ElementMapOption>[]) => {
    const element = props.vectorElement;
    if (!element) return;

    const vectors: UserVector[] = [];
    let newPosition = 1;
    const userVectors = new Map(model.vectors.map((v) => [v.position + 1, v]));

    options.forEach(({ id }, index) => {
        const vector = userVectors.get(id!)!;

        vectors.push({ ...vector, position: index });

        if (id === model.vectorOption) {
            newPosition = index + 1;
        }
    });

    element.vectors = [...vectors];

    if (props.editMode) {
        element.setEditedProp('vectors');
    }

    initVectorMap(vectors);
    loadVectorIntoElement(newPosition);
};

const removeVectorLibrary = () => {
    const element = props.vectorElement;
    if (!element) return;

    if (props.editMode) {
        element.setEditedProp('vectorLibraryId');
    }

    element.vectorLibraryId = undefined;
    model.vectorLibraryId = undefined;
    loadVectorIntoElement(1);
};

const updateElementId = () => {
    const element = props.vectorElement;
    if (!element) return;
    element.id = model.elementId;
};

onUnmounted(() => {
    props.canvas.off('vectors:pasted', onVectorPasted);
});

const currentSlot = computed(() => (model.vectorLibraryId ? 'header' : 'actions'));

return { __sfc: true,VECTOR_TYPE, props, emit, model, cachedSvg, disabled, onVectorPasted, initVectorMap, displayLoader, onVectorUpload, loadVectorIntoElement, removeVectorOptionFromMap, onVectorOptionsReordered, removeVectorLibrary, updateElementId, currentSlot, ElementLibrarySelector, CInput, CButton }
}

})